import React from 'react';
import { CountryCode } from '@dayetopia/library';
import {
  ProductBuyOption,
  ProductBuyOptionInfo,
  ProductBuyOptionSubscription,
  ProductConfiguration,
  ProductRawOption,
  VariantItem
} from '@contracts';
import Drop1 from '@images/icon-drop.svg';
import Drop2 from '@images/icon-drop2.svg';
import Drop3 from '@images/icon-drop3.svg';
import { showInCountryCondition } from '@utils/country';

/**
 * Retrieves the title for a product buy option.
 * @param {boolean} oneOff - Whether the option is for a one-off purchase.
 * @param {number} subscriptionIntervalLength - The length of the subscription interval.
 * @returns {string} The title for the product buy option.
 */
export function getProductBuyOptionTitle(oneOff: boolean, subscriptionIntervalLength?: number) {
  return oneOff ? 'components.buy-one' : getProductBuyOptionSubscriptionTitle(subscriptionIntervalLength);
}

/**
 * Retrieves the title for a product buy option subscription.
 * @param {number} subscriptionIntervalLength - The length of the subscription interval.
 * @returns {string} The title for the product buy option subscription.
 */
function getProductBuyOptionSubscriptionTitle(subscriptionIntervalLength?: number) {
  if (subscriptionIntervalLength === 1) {
    return 'components.subscription.monthly-plan';
  }

  if (subscriptionIntervalLength === 3) {
    return 'components.subscription.quarterly-plan';
  }

  // Check for common subscription intervals with specific keys
  if (subscriptionIntervalLength === 2 || subscriptionIntervalLength === 6 || subscriptionIntervalLength === 12) {
    return `components.subscription.${subscriptionIntervalLength}-month-plan`;
  }

  // Fallback to generic n-month-plan key for other intervals
  return 'components.subscription.n-month-plan';
}

/**
 * Checks if the product buy option is selected.
 * @param {string | null | undefined} selectedProductId - The selected product id.
 * @param {string | null | undefined} selectedPlanCode - The selected plan code.
 * @param {string} optionProductId - The option product id.
 * @param {boolean} isOneOff - Whether the option is for a one-off purchase.
 * @param {ProductBuyOptionSubscription} optionSubscription - The option subscription.
 * @returns {boolean} True if the product buy option is selected, otherwise false.
 */
export function getIsSelectedProductBuyOption(
  selectedProductId: string | null | undefined,
  selectedPlanCode: string | null | undefined,
  optionProductId: string | null | undefined,
  isOneOff: boolean,
  optionSubscription?: ProductBuyOptionSubscription
) {
  if (selectedProductId !== optionProductId) {
    return false;
  }

  if (isOneOff) {
    return !selectedPlanCode;
  }

  return selectedPlanCode === optionSubscription?.subscriptionPlan;
}

export function generateProductBuyOptions(
  productOptions: ProductRawOption[],
  configurations: ProductConfiguration[],
  country: CountryCode
): ProductBuyOption[] {
  if (!Array.isArray(configurations) || !Array.isArray(productOptions)) {
    return [];
  }

  const productIdHasVariant = (input: string, variant: string): boolean => new RegExp(`\\d+${variant}`).test(input);

  const getProductBuyOptionInfoArray = (optionInfo: string): ProductBuyOptionInfo[] => {
    try {
      const infoObject = JSON.parse(optionInfo);
      const { content } = infoObject;
      return content;
    } catch (e) {
      return [];
    }
  };

  const getProductOptionIcon = (sku: string) => {
    if (productIdHasVariant(sku, 'sp')) {
      return <Drop3 />;
    }
    if (productIdHasVariant(sku, 'cs') || productIdHasVariant(sku, 'ns') || productIdHasVariant(sku, 's')) {
      return <Drop2 />;
    }
    return <Drop1 />;
  };

  const getSubsFromProductOptions = (productOption: ProductRawOption): ProductBuyOptionSubscription[] => {
    const subscriptions: ProductBuyOptionSubscription[] = [];

    if (productOption.subscription_sku?.text && showInCountryCondition(productOption.subscription_countries, country)) {
      subscriptions.push({
        subscriptionSku: productOption.subscription_sku?.text || '',
        subscriptionPlan: productOption.subscription_plan?.text || '',
        subscriptionIncludes: productOption.subscription_includes?.text?.length ? productOption.subscription_includes?.text.split(',') : [],
        subscriptionCount: 0
      });
    }

    if (productOption.subscription_b_sku?.text && showInCountryCondition(productOption.subscription_b_countries, country)) {
      subscriptions.push({
        subscriptionSku: productOption.subscription_b_sku?.text || '',
        subscriptionPlan: productOption.subscription_b_plan?.text || '',
        subscriptionIncludes: productOption.subscription_b_includes?.text?.length ? productOption.subscription_b_includes?.text.split(',') : [],
        subscriptionCount: 0
      });
    }

    return subscriptions;
  };

  const productBuyOptions: ProductBuyOption[] = [];

  productOptions.forEach((productOption) => {
    const productOneOffSku = productOption.one_off_sku?.text || '';
    const subscriptions: ProductBuyOptionSubscription[] = getSubsFromProductOptions(productOption);
    const { oneOffCount, subscriptionCounts, variants } = calculateCounts(productOneOffSku, subscriptions, configurations);

    const productBuyOption: ProductBuyOption = {
      oneOffSku: productOneOffSku || '',
      oneOffCount,
      subscriptions: subscriptions.map((sub, i) => ({ ...sub, subscriptionCount: subscriptionCounts[i] })),
      label: productOption.label?.text || '',
      title: productOption.title?.text || '',
      icon: getProductOptionIcon(productOneOffSku ?? subscriptions?.[0]?.subscriptionSku ?? ''),
      variants,
      optionInfo: getProductBuyOptionInfoArray(productOption.option_info_json?.text ?? ''),
      hideWhatsInsideSection: productOption.hide_whats_inside_section ?? false
    };
    productBuyOptions.push(productBuyOption);
  });

  return productBuyOptions;
}

export function getFlattenedConfiguration(config: ProductConfiguration | undefined, configurations: ProductConfiguration[]): VariantItem[] {
  if (!config || !config.configuration) {
    return [];
  }

  return config.configuration.flatMap((item) => {
    const innerConfig = configurations.find((c) => c.variant === item.variant);

    if (innerConfig) {
      const newConfig = innerConfig.configuration || [];

      if (item.count && item.count > 1) {
        return newConfig.map((i) => ({ ...i, count: i.count ? i.count * item.count : item.count }));
      }

      return newConfig;
    }
    return [item];
  });
}

export function calculateCounts(oneOffSku: string | null, subscriptions: ProductBuyOptionSubscription[], configurations: ProductConfiguration[]) {
  const oneOffConfig = configurations.find((c) => c.variant === oneOffSku);
  const subscriptionConfigs = subscriptions.map((sub) => configurations.find((c) => c.variant === sub.subscriptionSku));
  const subscriptionsVariants = subscriptionConfigs.map((conf) => getFlattenedConfiguration(conf, configurations));
  const oneOffVariants = getFlattenedConfiguration(oneOffConfig, configurations);

  const classesSet1 = new Set(['pnp', 'pr', 'pn', 'pl']);
  const classesSet2 = new Set(['cbdsuper', 'cbdregular', 'nakedsuper', 'nakedregular']);

  // Check if all items belong to the same class set
  const allOfSameClassSet = (items: VariantItem[]) => {
    // Determine which class sets the items belong to
    const belongToSet1 = items.every((item) => classesSet1.has(item.variant));
    const belongToSet2 = items.every((item) => classesSet2.has(item.variant));

    // Ensure all items belong to only one of the class sets, not both or neither
    return (belongToSet1 && !belongToSet2) || (!belongToSet1 && belongToSet2);
  };

  const sumCountIfSameClassSet = (items: VariantItem[]) => {
    if (allOfSameClassSet(items)) {
      return items.reduce((acc, item) => acc + item.count, 0);
    }
    return 0;
  };

  const subscriptionCounts: number[] = subscriptionsVariants.map((variants) => (variants ? sumCountIfSameClassSet(variants) : 0));
  const oneOffCount: number = oneOffVariants ? sumCountIfSameClassSet(oneOffVariants) : 0;

  const variants: { [key: string]: VariantItem[] } = {};

  subscriptions.forEach((sub, i) => {
    if (sub.subscriptionPlan && subscriptionsVariants[i]?.length) {
      variants[sub.subscriptionPlan] = subscriptionsVariants[i];
    }
  });

  if (oneOffSku && oneOffVariants.length > 0) {
    variants['one-off'] = oneOffVariants;
  }

  return {
    subscriptionCounts,
    oneOffCount,
    variants
  };
}
